import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CatalogPage } from './pages/CatalogPage';
import { CartPage } from './pages/CartPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { CheckoutSuccessPage } from './pages/CheckoutSuccessPage';
import { CheckoutFailurePage } from './pages/CheckoutFailurePage';
import { ProductPage } from './pages/ProductPage';
import { OrdersPage } from './pages/OrdersPage';
import { OrderDetailsPage } from './pages/OrderDetailsPage';
import { AdminDashboard } from './pages/AdminDashboard';
import { AdminOrdersPage } from './pages/AdminOrdersPage';
import { AdminOrderDetailPage } from './pages/AdminOrderDetailPage';
import { AdminProductsPage } from './pages/AdminProductsPage';
import { AdminProductEditPage } from './pages/AdminProductEditPage';
import { AdminProductCreatePage } from './pages/AdminProductCreatePage';
import { AdminCategoriesPage } from './pages/AdminCategoriesPage';
import { AdminCategoryEditPage } from './pages/AdminCategoryEditPage';
import { AdminCategoryCreatePage } from './pages/AdminCategoryCreatePage';
import { Loading } from './components/Loading';
import { customersApi } from './api';
import { usePlatform } from './hooks/usePlatform';
import { CustomerContext } from './contexts/CustomerContext';
import { AlertProvider } from './contexts/AlertContext';
import type { Customer } from './types';
import './styles/globals.css';

function App() {
  const platform = usePlatform();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Notify the host SDK we're ready (no-op on platforms that don't expose ready()).
        platform.ready();

        // Telegram-specific UI configuration.
        if (platform.platform === 'telegram') {
            const tg = window.Telegram?.WebApp;
            if (tg) {
                tg.expand();

                // Disable vertical swipes to close
                if (tg.enableClosingConfirmation) {
                    tg.enableClosingConfirmation();
                }

                // Set header color if available
                if (tg.setHeaderColor) {
                    tg.setHeaderColor('#FFFFFF');
                }

                // Set background color if available
                if (tg.setBackgroundColor) {
                    tg.setBackgroundColor('#F5F9F6');
                }

                // Request write access permission for bot to send messages
                // (Telegram-only API)
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const tgAny = tg as any;
                if (tgAny.requestWriteAccess) {
                    tgAny.requestWriteAccess((granted: boolean) => {
                        if (granted) {
                            console.log('Пользователь разрешил боту отправлять сообщения');
                        } else {
                            console.log('Пользователь отклонил запрос на отправку сообщений');
                        }
                    });
                }
            }
        }

        // MAX-specific UI configuration.
        // MAX has no expand() / setHeaderColor / setBackgroundColor / requestWriteAccess.
        // Background color is controlled via CSS in this codebase.
        if (platform.platform === 'max') {
            window.WebApp?.enableClosingConfirmation?.();
        }

        // Load customer data (sent through whichever auth header is appropriate).
        loadCustomer();
    // We intentionally run this once on mount: platform identity does not
    // change during a session, and we want loadCustomer to fire exactly once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

  const loadCustomer = async () => {
    try {
      setLoading(true);
      const data = await customersApi.getMe();
      setCustomer(data);
      console.log('Customer loaded:', data);
    } catch (err) {
      console.error('Failed to load customer:', err);
      // If error, continue as regular user
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <Loading />
      </div>
    );
  }

  return (
    <AlertProvider>
      <CustomerContext.Provider value={customer}>
        <Router>
        <Routes>
          <Route path="/" element={<CatalogPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
          <Route path="/checkout/failure" element={<CheckoutFailurePage />} />
          <Route path="/product/:id" element={<ProductPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/orders/:id" element={<OrderDetailsPage />} />
          <Route
            path="/admin"
            element={
              customer?.is_admin ? (
                <AdminDashboard />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/admin/orders"
            element={
              customer?.is_admin ? (
                <AdminOrdersPage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/admin/orders/:id"
            element={
              customer?.is_admin ? (
                <AdminOrderDetailPage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/admin/products"
            element={
              customer?.is_admin ? (
                <AdminProductsPage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/admin/products/create"
            element={
              customer?.is_admin ? (
                <AdminProductCreatePage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/admin/products/:id/edit"
            element={
              customer?.is_admin ? (
                <AdminProductEditPage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/admin/categories"
            element={
              customer?.is_admin ? (
                <AdminCategoriesPage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/admin/categories/create"
            element={
              customer?.is_admin ? (
                <AdminCategoryCreatePage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/admin/categories/:id/edit"
            element={
              customer?.is_admin ? (
                <AdminCategoryEditPage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
        </Routes>
      </Router>
    </CustomerContext.Provider>
    </AlertProvider>
  );
}

export default App;
